import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { RxIconModule } from '@nrwl/rx-icon';
import { parseLocalDateString } from '../../../../helpers/date-utils';
import {
  ScribeSuggestionIndicatorComponent,
  ScribeSuggestionIndicatorVariant,
} from '../../shared/scribe-suggestion-indicator/scribe-suggestion-indicator.component';
import { AmendmentFieldIndicatorComponent } from '../../shared/amendment-field-indicator/amendment-field-indicator.component';
import { ScribeSuggestionMarker } from '../../../scribe-suggestion-marker.model';
import { clearScribeSuggestionMarker } from '../../../scribe-suggestion-marker.util';

export type InputField = {
  title: string;
  value: string;
  placeholder?: string;
  required?: boolean;
  type?: string;
  hasConflict?: boolean;
  conflictTooltip?: string;
  id?: string;
  isScribeSuggested?: ScribeSuggestionMarker;
};

@Component({
  selector: 'fb-input-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    RxIconModule,
    ScribeSuggestionIndicatorComponent,
    AmendmentFieldIndicatorComponent,
  ],
  template: `
    <div class="fb-component">
      <!-- Header -->
      <div class="fb__header">
        <div class="fb__title">{{ label }}</div>
        <div class="fb__note" *ngIf="populatedFrom && !isPrinting">
          * populated from {{ populatedFrom }}
        </div>
      </div>

      <div class="fb__rows" [class.readonly]="_disabled || readonly">
        <div
          class="fb__row"
          *ngFor="let row of renderedValues; let i = index; trackBy: trackByIndex"
          [class.fb__row--empty]="i === 0 && isRowEmpty(row)"
          (click)="onEmptyRowClick(i, $event)"
        >
          <ng-container *ngFor="let field of fields; let fieldIndex = index">
            <div class="fb__field" [ngClass]="'fb__' + (field.type || 'text')">
              <div class="fb__label">
                <span class="fb__label-content">
                  <span>{{ field.title }}</span>
                  <fb-scribe-suggestion-indicator
                    [target]="row[fieldIndex]"
                    [variant]="scribeIndicatorVariant"
                  ></fb-scribe-suggestion-indicator>
                  <fb-amendment-field-indicator
                    [fieldKey]="amendmentCategory + ':r' + i + ':f' + fieldIndex"
                  />
                </span>
              </div>

              <!-- Date field -->
              <ng-container *ngIf="field.type === 'date'">
                <div class="fb__date-wrap">
                  <input
                    class="fb__input"
                    [ngClass]="{ 'has-conflict': !isPreview && row[fieldIndex].hasConflict }"
                    type="text"
                    placeholder="MM/DD/YYYY"
                    [matDatepicker]="picker"
                    [(ngModel)]="row[fieldIndex].value"
                    (input)="onDateInputForRow(row, fieldIndex, $event)"
                    (ngModelChange)="onDateChangeForRow(row, fieldIndex, $event)"
                    [disabled]="_disabled || readonly"
                    [attr.aria-label]="field.title"
                    (focus)="picker.open()"
                  />
                  <button
                    type="button"
                    class="fb-btn fb-btn--icon fb-btn--calendar"
                    [disabled]="_disabled || readonly"
                    aria-label="Open calendar"
                    (click)="picker.open()"
                  >
                    <rx-icon name="icon-calendar2"></rx-icon>
                  </button>
                  <rx-icon
                    *ngIf="!isPreview && row[fieldIndex].hasConflict"
                    name="icon-warning-circle"
                    class="conflict-warning-icon"
                    [title]="row[fieldIndex].conflictTooltip || 'Conflict detected'"
                  ></rx-icon>
                  <mat-datepicker #picker></mat-datepicker>
                </div>
              </ng-container>

              <!-- Text field (default) -->
              <ng-container *ngIf="!field.type || field.type === 'text'">
                <div class="fb__input-wrapper">
                  <input
                    class="fb__input"
                    [ngClass]="{ 'has-conflict': !isPreview && row[fieldIndex].hasConflict }"
                    type="text"
                    [(ngModel)]="row[fieldIndex].value"
                    (ngModelChange)="onFieldChanged(row[fieldIndex])"
                    [placeholder]="field.placeholder || 'Enter ' + field.title.toLowerCase()"
                    [disabled]="_disabled || readonly || isNameField(field.title)"
                    [attr.aria-label]="field.title"
                  />
                  <rx-icon
                    *ngIf="!isPreview && row[fieldIndex].hasConflict"
                    name="icon-warning-circle"
                    class="conflict-warning-icon"
                    [title]="row[fieldIndex].conflictTooltip || 'Conflict detected'"
                  ></rx-icon>
                </div>
              </ng-container>
            </div>
          </ng-container>

          <!-- Remove button -->
          <div class="fb__field fb__field--action" *ngIf="!isPrinting">
            <button
              type="button"
              class="fb-btn fb-btn--icon fb-btn--remove"
              aria-label="Remove item"
              (click)="removeRow(i)"
              [disabled]="_disabled || readonly"
            >
              <rx-icon name="icon-close"></rx-icon>
            </button>
          </div>
        </div>
      </div>

      <!-- Add row -->
      <button
        type="button"
        class="fb-btn fb-btn--add"
        (click)="addRow()"
        [disabled]="_disabled || readonly"
        *ngIf="!isPrinting"
      >
        <span class="fb-btn__icon"
          ><rx-icon name="icon-circle-plus-2" [size]="16" class="blue"></rx-icon
        ></span>
        <span>{{ addTitle || 'Add Item' }}</span>
      </button>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        font-family: Roboto, Arial, sans-serif;
      }

      /* Component Container */
      .fb-component {
        width: 100%;
      }

      /* Header */
      .fb__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 17px;
      }

      .fb__title {
        font-family: Roboto, Arial, sans-serif;
        font-weight: 500;
        font-size: 14px;
        line-height: 100%;
        color: #000;
        page-break-after: avoid;
        break-after: avoid;
      }

      .fb__note {
        font-size: 12px;
        color: #6b7280;
        margin-left: 16px;
      }

      /* Rows & Fields */
      .fb__rows {
        display: flex;
        flex-direction: column;
        gap: 10px;
      }

      .fb__row {
        display: flex;
        align-items: flex-end;
        gap: 10px;
        position: relative;
      }

      .fb__row--empty {
        cursor: pointer;
      }

      .fb__row--empty .fb__input,
      .fb__row--empty .fb-btn {
        pointer-events: none;
        opacity: 0.7;
      }

      .fb__row--empty:hover {
        background: rgba(0, 97, 253, 0.03);
        border-radius: 6px;
      }

      .fb__field {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }

      .fb__field--action {
        display: flex;
        align-items: flex-end;
      }

      .fb__date {
        width: 150px;
      }

      .fb__text {
        flex: 1 1 auto;
      }

      .fb__label {
        font-family: Roboto, Arial, sans-serif;
        font-weight: 400;
        font-size: 12px;
        line-height: 16px;
        letter-spacing: 0.01em;
        color: #70737d;
        break-after: avoid;
      }

      .fb__label-content {
        position: relative;
        display: inline-flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 4px 8px;
        max-width: 100%;
        min-width: 0;
      }

      .fb__label-content > :first-child {
        min-width: 0;
        overflow-wrap: anywhere;
      }

      /* Inputs */
      .fb__input {
        height: 40px;
        width: 100%;
        padding: 0 12px;
        border: 1px solid #d2d6df;
        border-radius: 6px;
        background: #fff;
        outline: none;
        font-size: 14px;
        line-height: 20px;
      }

      .fb__input::placeholder {
        color: #70737d;
      }

      .fb__input:focus {
        border-color: #0061fd;
        box-shadow: 0 0 0 2px rgba(0, 97, 253, 0.15);
      }

      .fb__input.has-conflict {
        border-color: #f1a23b !important;
      }

      .fb__input-wrapper {
        position: relative;
        width: 100%;
      }

      .conflict-warning-icon {
        position: absolute;
        right: 10px;
        top: 50%;
        transform: translateY(-50%);
        color: #f1a23b;
        z-index: 1;
        pointer-events: auto;
        cursor: help;

        ::ng-deep svg path {
          fill: #f1a23b;
        }

        ::ng-deep svg {
          width: 20px;
          height: 20px;
        }
      }

      .fb__date-wrap .conflict-warning-icon {
        right: 30px;
      }

      /* Date input with calendar */
      .fb__date-wrap {
        position: relative;
        display: flex;
        align-items: center;
      }

      .fb__date-wrap .fb-btn--calendar {
        position: absolute;
        right: 0;
        width: 28px;
        height: 28px;
      }

      .fb__date-wrap .fb__input {
        padding-right: 40px;
      }

      /* Buttons */
      .fb-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        border: none;
        cursor: pointer;
        padding: 0;
      }

      .fb-btn--icon {
        width: 40px;
        height: 40px;
        ::ng-deep {
          .mat-icon svg path {
            fill: transparent;
          }
        }
      }

      .fb-btn--icon:hover {
        background: rgba(0, 97, 253, 0.06);
        border-radius: 50%;
      }

      .fb-btn--icon:active {
        background: rgba(0, 97, 253, 0.12);
      }

      .fb-btn--add {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        margin-top: 8px;
        padding: 4px 0;
        font-size: 14px;
        line-height: 20px;
        color: #0b5bff;
        span {
          font-size: 14px;
        }
      }

      .fb-btn--remove {
        ::ng-deep {
          .mat-icon svg path {
            fill: #777;
          }
        }
      }

      .fb-btn__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      @media print {
        .fb__input::placeholder {
          color: transparent !important;
        }

        .fb__date-wrap .fb-btn--calendar,
        .conflict-warning-icon {
          display: none !important;
        }

        .fb__date-wrap .fb__input {
          padding-right: 12px;
        }
      }

      /* Responsive */
      // @media (max-width: 640px) {
      //     .fb__row {
      //         flex-direction: column;
      //         align-items: stretch;
      //     }

      //     .fb-btn--remove {
      //         align-self: flex-end;
      //     }

      //     .fb__date {
      //         width: 100%;
      //     }
      // }
    `,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FbInputListComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FbInputListComponent implements ControlValueAccessor {
  /**
   * Name of the medical history category this component edits, for the amendment indicator.
   */
  @Input() amendmentCategory = '';
  @Input() label = 'List Items';
  @Input() addTitle = '';
  @Input() populatedFrom: string = 'PN';
  @Input() readonly = false;
  @Input() isPreview = false;
  @Input() isPrinting = false;
  @Input() showEmptyRowsInPrint = false;
  @Input() scribeIndicatorVariant: ScribeSuggestionIndicatorVariant = 'full';

  private isDeleting = false;

  constructor(private cd: ChangeDetectorRef) {}

  @Input() set values(values: any[]) {
    if (!Array.isArray(values)) {
      this._values = [];
    } else {
      this._values = values;
    }

    if (!this.isPrinting && this._values.length === 0) {
      this._values = [this.createEmptyRow()];
    }

    this.valuesChange.emit(this._values);
    this.onChange(this._values);
    this.onTouched();
  }
  get values(): any[] {
    return this._values;
  }

  get renderedValues(): any[] {
    if (!this.isPrinting) {
      return this._values;
    }

    if (this.showEmptyRowsInPrint) {
      return this._values;
    }

    return this._values.filter((row) => !this.isRowEmpty(row));
  }

  @Output() valuesChange = new EventEmitter<any[]>();
  @Output() addClick = new EventEmitter<void>();
  @Output() emptyRowClick = new EventEmitter<void>();

  @Input() set fields(value: InputField[]) {
    this._fields = value || [];
  }
  get fields(): InputField[] {
    return this._fields;
  }

  private _fields: InputField[] = [
    { title: 'Date', value: '', required: true, type: 'date' },
    { title: 'Condition', value: '', required: true },
  ];
  private _values: any[] = [];

  _disabled = false;

  onChange: (value: any[]) => void = () => {};
  onTouched: () => void = () => {};

  writeValue(value: any[] | null): void {
    if (!value || !Array.isArray(value)) {
      this._values = this.isPrinting ? [] : [this.createEmptyRow()];
      return;
    }

    this._values = value.length > 0 ? value : this.isPrinting ? [] : [this.createEmptyRow()];

    console.log('Input-list writeValue received:', value);

    if (value.length > 0 && Array.isArray(value[0])) {
      return;
    }

    this._values = this._values.map((row) => {
      if (!Array.isArray(row)) {
        return this.createEmptyRow();
      }

      return this._fields.map((field, index) => {
        if (index < row.length && row[index] && typeof row[index] === 'object') {
          return { ...field, ...row[index] };
        }
        return { ...field };
      });
    });
  }
  registerOnChange(fn: (value: any[]) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled;
  }

  private createEmptyRow(): InputField[] {
    return this._fields.map((field) => ({
      ...field,
      value: '',
    }));
  }

  addRow(prefill?: InputField[]) {
    if (this.addClick.observers.length > 0) {
      this.addClick.emit();
      return;
    }

    const emptyRow = prefill || this.createEmptyRow();
    this._values = [...this._values, emptyRow];
    this.onValuesChanged();
  }

  removeRow(index: number) {
    this.isDeleting = true;
    if (this._values.length === 1) {
      this._values = [this.createEmptyRow()];
    } else {
      this._values = this._values.filter((_, i) => i !== index);
    }
    this.onValuesChanged();

    setTimeout(() => {
      this.isDeleting = false;
      this.cd.detectChanges();
    }, 100);
  }

  onValuesChanged() {
    const next = [...this._values];
    this.valuesChange.emit(next);
    this.onChange(next);
    this.onTouched();
  }

  onFieldChanged(field: InputField): void {
    clearScribeSuggestionMarker(field);
    this.onValuesChanged();
  }

  onDateChange(rowIndex: number, fieldIndex: number, value: any) {
    const target = this._values[rowIndex]?.[fieldIndex];
    if (target) {
      clearScribeSuggestionMarker(target);
    }
    this._values[rowIndex][fieldIndex].value = this.coerceDateValue(value);

    this.onValuesChanged();
  }

  onDateChangeForRow(row: InputField[], fieldIndex: number, value: any) {
    const nextValue = this.coerceDateValue(value);
    clearScribeSuggestionMarker(row[fieldIndex]);

    this._values = this._values.map((currentRow) =>
      currentRow === row
        ? currentRow.map((field, index) =>
            index === fieldIndex ? { ...field, value: nextValue } : field,
          )
        : currentRow,
    );

    this.onValuesChanged();
  }

  onDateInputForRow(row: InputField[], fieldIndex: number, event: Event) {
    const rawValue = (event.target as HTMLInputElement | null)?.value || '';
    if (rawValue.trim() !== '') {
      return;
    }

    clearScribeSuggestionMarker(row[fieldIndex]);
    this._values = this._values.map((currentRow) =>
      currentRow === row
        ? currentRow.map((field, index) => (index === fieldIndex ? { ...field, value: '' } : field))
        : currentRow,
    );

    this.onValuesChanged();
  }

  trackByIndex(index: number) {
    return index;
  }

  onEmptyRowClick(rowIndex: number, event: MouseEvent) {
    if (this.isDeleting) {
      return;
    }

    if (rowIndex === 0 && this.isRowEmpty(this._values[rowIndex])) {
      event.stopPropagation();

      if (this.emptyRowClick.observers.length > 0) {
        this.emptyRowClick.emit();
      } else if (this.addClick.observers.length > 0) {
        this.addClick.emit();
      }
    }
  }

  isRowEmpty(row: InputField[]): boolean {
    if (!row || !Array.isArray(row)) return true;
    return row.every((field) => !field.value || field.value === '');
  }

  replaceEmptyRowWithData(data: InputField[]) {
    if (this._values.length > 0 && this.isRowEmpty(this._values[0])) {
      this._values[0] = data;
    } else {
      this._values = [...this._values, data];
    }
    this.onValuesChanged();

    this.cd.markForCheck();
    this.cd.detectChanges();
  }

  private coerceDate(value: any): Date | null {
    if (!value) return null;
    if (value instanceof Date) return value;
    const s = String(value).trim();
    if (!s) return null;
    const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (m) {
      const mm = +m[1],
        dd = +m[2],
        yyyy = +m[3];
      const d = new Date(yyyy, mm - 1, dd);
      return isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }

  private coerceDateValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'string') {
      return value.trim() === '' ? '' : value;
    }

    const parsedDate = parseLocalDateString(value);
    const match = /^(\d{4})-\d{2}-\d{2}$/.exec(parsedDate);
    return match && Number(match[1]) >= 1910 ? parsedDate : '';
  }

  isNameField(title: string): boolean {
    return title === 'Condition' || title === 'Allergy' || title === 'Surgery';
  }
}
